import { useSyncExternalStore } from 'react';
import {
  effectiveHubAlertRing,
  isCentralAssignmentAlert,
  type HubAlert,
  type HubAlertLink,
} from '../hubAlertRules';
import { shiftDayStart, type Briefing } from '../hubBriefingRules';
import { startHubAlarm, stopHubAlarm } from '../hubAlarm';

/**
 * Hub's alert center: one place for every order alert of the working day and for what is waiting,
 * shared by the alert pill, the header bell and the alerts drawer (which live in different parts of
 * the page). A module store, not React state: HubAlerts (lazily loaded) writes, the header reads.
 *
 * Alert states:
 * - `ringing`: a loud alert nobody has acknowledged; the alarm loops while any is ringing. Only
 *   this page load rings: after a reload (or a forced update) it comes back as `new`.
 * - `new`: arrived without ringing (quiet type, another tab rang it, or restored after a reload)
 *   and not yet looked at; counted on the bell.
 * - `seen`: acknowledged or looked at in the drawer; kept for the rest of the working day.
 * - `opened`: its page was opened.
 *
 * An alert that does not ring here (another facility than the one selected, see
 * alertPresentation) arrives as `new` with a short calm notice instead of the ringing bar.
 */

export type AlertState = 'ringing' | 'new' | 'seen' | 'opened';
export type AlertItem = { alert: HubAlert; state: AlertState; at: number };
export type DrawerTab = 'alerts' | 'waiting';
/**
 * A calm bar for a few seconds, never ringing: `arrived` for an alert that did not ring here,
 * `here` for unread alerts of the facility the operator just switched to.
 */
export type AlertNotice = { kind: 'arrived' | 'here'; keys: ReadonlyArray<string>; at: number };

type State = {
  userId: string | null;
  items: ReadonlyArray<AlertItem>;
  waiting: Briefing | null;
  /** A new Waiting update the operator has not opened yet (dot on the bell). */
  waitingUnseen: boolean;
  /** This page may play sound (a tap armed it); not saved. */
  soundReady: boolean;
  /**
   * `fresh`: alerts that were new when the Alerts tab was looked at (shown as New while the
   * drawer stays open). `auto`: opened by itself (a waiting list), so it does not take focus.
   */
  drawer: { open: boolean; tab: DrawerTab; fresh: ReadonlyArray<string>; auto: boolean };
  /** Not saved: a notice belongs to this page load. */
  notice: AlertNotice | null;
};

type Saved = { items?: Array<AlertItem>; waiting?: Briefing | null; waitingUnseen?: boolean };

/** A working day of alerts is plenty; older ones drop off (alerts not yet looked at last). */
export const MAX_ITEMS = 50;
/**
 * The same alert key pushed again after this long (and after it was looked at) is a new
 * occurrence, for example a package back in WIP: it alerts again. Matches the push duplicate window.
 */
export const REPEAT_AFTER_MS = 10 * 60 * 1000;
const STATES: ReadonlyArray<AlertState> = ['ringing', 'new', 'seen', 'opened'];
/** How far along an alert is, for merging what two tabs saved. */
const PROGRESS: Record<AlertState, number> = { ringing: 0, new: 0, seen: 1, opened: 2 };
const STORAGE_PREFIX = 'hub-alerts:inbox:';
const storageKey = (userId: string) => `${STORAGE_PREFIX}${userId}`;

const initialState = (): State => ({
  userId: null,
  items: [],
  waiting: null,
  waitingUnseen: false,
  soundReady: false,
  drawer: { open: false, tab: 'alerts', fresh: [], auto: false },
  notice: null,
});

let state: State = initialState();
const listeners = new Set<() => void>();

/**
 * Saving merges with what other Hub tabs saved for this operator, so a tab that missed an alert
 * (still loading, frozen in the background) never erases it: per alert the newer occurrence wins,
 * then the one further along (opened over seen over unread).
 */
const merged = (mine: ReadonlyArray<AlertItem>, theirs: ReadonlyArray<AlertItem>, now: number) => {
  const byKey = new Map(mine.map(item => [item.alert.key, item]));
  theirs.forEach(other => {
    const own = byKey.get(other.alert.key);
    if (
      !own ||
      other.at > own.at ||
      (other.at === own.at && PROGRESS[other.state] > PROGRESS[own.state])
    ) {
      byKey.set(other.alert.key, other);
    }
  });
  return today(
    [...byKey.values()].sort((a, b) => b.at - a.at),
    now,
  );
};

const persist = () => {
  if (!state.userId) return;
  try {
    const now = Date.now();
    const saved: Saved = {
      items: merged(state.items, readSaved(state.userId).items ?? [], now),
      waiting: state.waiting,
      waitingUnseen: state.waitingUnseen,
    };
    localStorage.setItem(storageKey(state.userId), JSON.stringify(saved));
  } catch {
    // Storage full or blocked: the alert center keeps working in memory.
  }
};

const ringingCount = (current: State) =>
  current.items.filter(item => item.state === 'ringing').length;

/**
 * The alarm follows the store: a newly ringing alert (re)starts it, also after the alarm's own
 * time cap went quiet, and it stops once nothing is ringing.
 */
const syncAlarm = (previous: State) => {
  const before = ringingCount(previous);
  const now = ringingCount(state);
  if (now > before)
    startHubAlarm(() => {
      quietExpiredJitAssignments();
      return ringingCount(state) > 0;
    });
  if (now === 0 && before > 0) stopHubAlarm();
};

const update = (next: Partial<State>, options: { save?: boolean } = {}) => {
  const previous = state;
  state = { ...state, ...next };
  if (options.save !== false) persist();
  syncAlarm(previous);
  listeners.forEach(listener => listener());
};

/**
 * Today's alerts, newest first, at most MAX_ITEMS: over the cap, the oldest alerts already looked
 * at go first; one still ringing or unread is never dropped for space.
 */
const today = (items: ReadonlyArray<AlertItem>, now: number) => {
  const since = shiftDayStart(now);
  const todays = items.filter(item => item.at >= since);
  if (todays.length <= MAX_ITEMS) return todays;
  const unread = todays.filter(item => PROGRESS[item.state] === 0).length;
  let readRoom = Math.max(0, MAX_ITEMS - unread);
  return todays.filter(item => {
    if (PROGRESS[item.state] === 0) return true;
    readRoom -= 1;
    return readRoom >= 0;
  });
};

const todaysWaiting = (waiting: Briefing | null | undefined, now: number) =>
  waiting && waiting.at >= shiftDayStart(now) ? waiting : null;

const isNumber = (value: unknown) => typeof value === 'number' && Number.isFinite(value);

/** Saved data comes from storage (another build, a full disk, hand edits): only well-formed parts. */
const isAlertItem = (value: unknown): value is AlertItem => {
  const item = value as Partial<AlertItem> | null;
  const alert = item?.alert as Partial<HubAlert> | undefined;
  return (
    isNumber(item?.at) &&
    STATES.includes(item?.state as AlertState) &&
    typeof alert?.key === 'string' &&
    typeof alert.title === 'string' &&
    typeof alert.body === 'string' &&
    typeof alert.url === 'string' &&
    Array.isArray(alert.details)
  );
};

const isBriefing = (value: unknown): value is Briefing => {
  const briefing = value as Partial<Briefing> | null;
  return (
    isNumber(briefing?.at) &&
    Array.isArray(briefing?.facilities) &&
    briefing.facilities.every(
      facility =>
        isNumber(facility?.facilityId) &&
        typeof facility.facilityName === 'string' &&
        isNumber(facility.orders) &&
        isNumber(facility.pickups),
    )
  );
};

const parseSaved = (raw: string | null): Saved => {
  try {
    const saved = JSON.parse(raw ?? '{}') as Saved | null;
    return {
      items: Array.isArray(saved?.items) ? saved.items.filter(isAlertItem) : [],
      waiting: isBriefing(saved?.waiting) ? saved.waiting : null,
      waitingUnseen: saved?.waitingUnseen === true,
    };
  } catch {
    return {};
  }
};

const readSaved = (userId: string): Saved => {
  try {
    return parseSaved(localStorage.getItem(storageKey(userId)));
  } catch {
    return {};
  }
};

/**
 * The signed-in operator. A different operator (shared device) gets their own inbox: the previous
 * one's alerts and waiting list never show to the next person.
 */
export const bindUser = (userId: string | number | null | undefined, now = Date.now()) => {
  const id = userId === null || userId === undefined ? null : String(userId);
  if (id === state.userId) return;
  if (!id) {
    update({ ...initialState(), soundReady: state.soundReady }, { save: false });
    return;
  }
  const restored = readSaved(id);
  const waiting = todaysWaiting(restored.waiting, now);
  update(
    {
      ...initialState(),
      soundReady: state.soundReady,
      userId: id,
      // Never rings again after a reload: one tab already rang it (and the alarm's time cap may
      // have passed); it waits on the bell as new instead.
      items: today(restored.items ?? [], now).map(item =>
        item.state === 'ringing' ? { ...item, state: 'new' as const } : item,
      ),
      waiting,
      waitingUnseen: Boolean(waiting && restored.waitingUnseen),
    },
    { save: false },
  );
};

/** Drops yesterday's alerts and waiting list (a Hub left open overnight). */
export const pruneDay = (now = Date.now()) => {
  const items = today(state.items, now);
  const waiting = todaysWaiting(state.waiting, now);
  if (items.length === state.items.length && waiting === state.waiting) return;
  update({ items, waiting, waitingUnseen: waiting ? state.waitingUnseen : false });
};

/**
 * Another Hub tab saved this operator's inbox: take its waiting list when newer (a refresh or the
 * start-of-day briefing there), and its "seen" mark for the same list.
 */
export const syncWaitingFromStorage = (
  key: string | null,
  value: string | null,
  now = Date.now(),
) => {
  if (!state.userId || key !== storageKey(state.userId) || !value) return;
  const saved = parseSaved(value);
  const waiting = todaysWaiting(saved.waiting, now);
  if (!waiting) return;
  if (!state.waiting || waiting.at > state.waiting.at) {
    update({ waiting, waitingUnseen: Boolean(saved.waitingUnseen) }, { save: false });
  } else if (waiting.at === state.waiting.at && !saved.waitingUnseen && state.waitingUnseen) {
    update({ waitingUnseen: false }, { save: false });
  }
};

export const addAlert = (alert: HubAlert, as: 'ringing' | 'new', now = Date.now()) => {
  if (isCentralAssignmentAlert(alert.type) && effectiveHubAlertRing(alert, now) === 'QUIET') {
    alert = { ...alert, ring: 'QUIET' };
    as = 'new';
  }
  const existing = state.items.find(item => item.alert.key === alert.key);
  const repeat = existing && PROGRESS[existing.state] > 0 && now - existing.at >= REPEAT_AFTER_MS;
  if (existing && !repeat) {
    // The same alert again (another push path): only an unread one can start ringing.
    if (
      as === 'ringing' &&
      existing.state === 'new' &&
      !(isCentralAssignmentAlert(existing.alert.type) && existing.alert.ring === 'QUIET')
    ) {
      update({
        items: state.items.map(item =>
          item.alert.key === alert.key ? { ...item, state: 'ringing' } : item,
        ),
      });
    }
    return;
  }
  update({
    items: today(
      [{ alert, state: as, at: now }, ...state.items.filter(item => item.alert.key !== alert.key)],
      now,
    ),
    // Arrived while the drawer is open: it belongs with the new ones there.
    drawer: state.drawer.open
      ? { ...state.drawer, fresh: [alert.key, ...state.drawer.fresh] }
      : state.drawer,
  });
};

/** Keep overdue central alerts unread/visible, not acknowledged; another warehouse alarm may continue. */
export const quietExpiredJitAssignments = (now = Date.now()) => {
  const expired = (item: AlertItem) =>
    item.state === 'ringing' &&
    isCentralAssignmentAlert(item.alert.type) &&
    effectiveHubAlertRing(item.alert, now) === 'QUIET';
  if (!state.items.some(expired)) return;
  update({
    items: state.items.map(item =>
      expired(item) ? { ...item, state: 'new', alert: { ...item.alert, ring: 'QUIET' } } : item,
    ),
  });
};

const setStates = (
  keys: ReadonlyArray<string> | 'all',
  from: ReadonlyArray<AlertState>,
  to: AlertState,
) => {
  const matches = (item: AlertItem) =>
    from.includes(item.state) && (keys === 'all' || keys.includes(item.alert.key));
  if (!state.items.some(matches)) return;
  update({ items: state.items.map(item => (matches(item) ? { ...item, state: to } : item)) });
};

/** Acknowledged (here, in another tab, or by dismissing the tray copy): stops ringing, stays listed. */
export const acknowledge = (keys: ReadonlyArray<string> | 'all') =>
  setStates(keys, ['ringing', 'new'], 'seen');

export const markOpened = (key: string) => setStates([key], ['ringing', 'new', 'seen'], 'opened');

/** Today's alerts for the order a notification link opens (any facility when it names none). */
export const keysForLink = (link: HubAlertLink) =>
  state.items
    .filter(
      ({ alert }) =>
        alert.type === link.type &&
        link.entityId !== null &&
        alert.entityId === link.entityId &&
        (link.facilityId === null || alert.facilityId === link.facilityId),
    )
    .map(item => item.alert.key);

/** What is waiting at the operator's facilities (start of day, or a refresh). */
export const setWaiting = (waiting: Briefing | null, options: { unseen?: boolean } = {}) =>
  update({ waiting, waitingUnseen: options.unseen ?? state.waitingUnseen });

/** The waiting list was looked at elsewhere (its tray copy was dismissed): no dot on the bell. */
export const markWaitingSeen = () => {
  if (state.waitingUnseen) update({ waitingUnseen: false });
};

export const setSoundReady = (soundReady: boolean) => {
  if (soundReady !== state.soundReady) update({ soundReady }, { save: false });
};

const unread = () =>
  state.items
    .filter(item => item.state === 'ringing' || item.state === 'new')
    .map(item => item.alert.key);

/**
 * Showing a tab is looking at it: the Alerts tab acknowledges what was ringing or new (listed as
 * New while the drawer stays open), the Waiting tab clears its dot. Returns the alerts it
 * acknowledged, for the other tabs and the tray (alertCenterEffects).
 */
const look = (tab: DrawerTab, drawer: State['drawer']): Array<string> => {
  if (tab === 'waiting') {
    update({ drawer, waitingUnseen: false });
    return [];
  }
  const fresh = unread();
  update({
    drawer: { ...drawer, fresh: [...fresh, ...drawer.fresh.filter(key => !fresh.includes(key))] },
    items: state.items.map(item =>
      fresh.includes(item.alert.key) ? { ...item, state: 'seen' } : item,
    ),
  });
  return fresh;
};

/**
 * Opens the drawer, on the Alerts tab unless nothing is new there and a waiting list is (`auto`:
 * opened by itself, not by the operator). Opening on the Waiting tab leaves alerts untouched, so a
 * waiting list never silences a ringing alert.
 */
export const openDrawer = (tab?: DrawerTab, options: { auto?: boolean } = {}) => {
  const nextTab =
    tab ?? (unread().length === 0 && state.waiting?.facilities.length ? 'waiting' : 'alerts');
  if (state.notice) update({ notice: null }, { save: false });
  return look(nextTab, {
    open: true,
    tab: nextTab,
    fresh: state.drawer.open ? state.drawer.fresh : [],
    auto: Boolean(options.auto),
  });
};

export const setDrawerTab = (tab: DrawerTab) => look(tab, { ...state.drawer, tab });

export const closeDrawer = () =>
  update({ drawer: { ...state.drawer, open: false, fresh: [], auto: false } }, { save: false });

/**
 * Shows the calm notice for these alerts, newest first (not while the drawer is open). Alerts
 * arriving while an `arrived` notice shows join it (`+1`); anything else replaces it.
 */
export const showNotice = (
  kind: AlertNotice['kind'],
  keys: ReadonlyArray<string>,
  now = Date.now(),
) => {
  // The open drawer already lists them as new.
  if (keys.length === 0 || state.drawer.open) return;
  const earlier =
    kind === 'arrived' && state.notice?.kind === 'arrived'
      ? state.notice.keys.filter(key => !keys.includes(key))
      : [];
  update({ notice: { kind, keys: [...keys, ...earlier], at: now } }, { save: false });
};

/** Hides the notice; with `at`, only that notice (a newer one stays). */
export const clearNotice = (at?: number) => {
  if (!state.notice || (at !== undefined && state.notice.at !== at)) return;
  update({ notice: null }, { save: false });
};

/** Alerts of the notice that are still unread (the bar hides once none is). */
export const noticeItems = (current: State) =>
  current.notice
    ? current.items.filter(
        item => current.notice?.keys.includes(item.alert.key) && item.state === 'new',
      )
    : [];

/** Reloads the waiting list (registered by HubAlerts, which owns the briefing). */
let waitingRefresh: (() => Promise<void>) | null = null;
export const registerWaitingRefresh = (refresh: (() => Promise<void>) | null) => {
  waitingRefresh = refresh;
};
export const refreshWaiting = () => waitingRefresh?.() ?? Promise.resolve();

export const alertCount = (current: State) =>
  current.items.filter(item => item.state === 'ringing' || item.state === 'new').length;

export const ringingItems = (current: State) =>
  current.items.filter(item => item.state === 'ringing');

export const ringingKeys = () => ringingItems(state).map(item => item.alert.key);

export const getAlertCenter = () => state;

export const subscribeAlertCenter = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useAlertCenter = () =>
  useSyncExternalStore(subscribeAlertCenter, getAlertCenter, getAlertCenter);

/**
 * The alert center failed to show this inbox: drop what was saved (so a reload does not fail
 * again) and anything ringing without a way to acknowledge it.
 */
export const discardInbox = () => {
  try {
    if (state.userId) localStorage.removeItem(storageKey(state.userId));
  } catch {
    // Nothing saved to drop.
  }
  update(
    {
      items: [],
      waiting: null,
      waitingUnseen: false,
      drawer: initialState().drawer,
      notice: null,
    },
    { save: false },
  );
};

/** Tests and logout. */
export const resetAlertCenter = () => {
  const previous = state;
  state = initialState();
  syncAlarm(previous);
  listeners.forEach(listener => listener());
};
